import React, { useState } from 'react';
import { X, Send, Store, User, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';

const MessageBusinessModal = ({ business, product, service, order, onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { sendMessage } = useCommunityStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsLoading(true);
    try {
      const messageData = {
        receiverId: business.userId,
        content: message.trim(),
        businessId: business.id,
        businessName: business.name,
        ...(product && { productId: product.id, productName: product.name }),
        ...(service && { serviceId: service.id, serviceTitle: service.title }),
        ...(order && { orderId: order.id, orderNumber: order.orderNumber })
      };

      await sendMessage(messageData);
      
      toast.success('Message sent successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[95vh] sm:max-h-[90vh]">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
              <h3 className="text-lg font-bold text-gray-800">Message Business</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Business Info */}
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
              {business?.logoUrl ? (
                <img src={business.logoUrl} alt={business.name} className="h-12 w-12 rounded-xl object-cover" />
              ) : (
                <Store className="h-6 w-6 text-white" />
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800">{business?.name}</h4>
              <p className="text-sm text-gray-500">{business?.category}</p>
            </div>
          </div>

          {/* Context Info */}
          {(product || service || order) && (
            <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start">
                <AlertCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Inquiring about:</p>
                  <p className="text-sm text-blue-700">
                    {product ? product.name : service ? service.title : `Order #${order?.orderNumber}`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Hi ${business?.name}, I'm interested in your ${product ? 'product' : service ? 'service' : 'business'}...`}
                rows={5}
                className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                maxLength={1000}
                disabled={isLoading}
              />
              <div className="mt-2 flex justify-between text-xs text-gray-500">
                <span>Be specific about your inquiry</span>
                <span>{message.length}/1000</span>
              </div>
            </div>

            {/* From/To Info */}
            <div className="bg-gray-50 rounded-xl p-3 text-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">From:</span>
                  <span className="font-medium text-gray-800">{user?.firstName} {user?.lastName}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Store className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">To:</span>
                  <span className="font-medium text-gray-800">{business?.name}</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Actions */}
        <div className="p-4 sm:p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !message.trim()}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageBusinessModal;